import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { GithubActionsApi } from '@backstage-community/plugin-github-actions';

type Options = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
};

/**
 * Implements the community GitHub Actions API using Backstage's authenticated
 * proxy. The proxy adds the read-only GitHub PAT on the server, so it is never
 * exposed to the browser.
 */
export class GithubActionsProxyClient implements GithubActionsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: Options) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async proxyUrl(path: string): Promise<string> {
    const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');
    return `${proxyBaseUrl}/github-actions/${path}`;
  }

  private async get<T>(path: string): Promise<T> {
    const response = await this.fetchApi.fetch(await this.proxyUrl(path));

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `GitHub API request failed: ${response.status} ${
          body || response.statusText
        }`,
      );
    }

    return response.json() as Promise<T>;
  }

  private repositoryPath(owner: string, repo: string): string {
    return `repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`;
  }

  async listWorkflowRuns(
    options: Parameters<GithubActionsApi['listWorkflowRuns']>[0],
  ): ReturnType<GithubActionsApi['listWorkflowRuns']> {
    const { owner, repo, pageSize = 100, page = 1, branch } = options;
    const query = new URLSearchParams({
      per_page: String(pageSize),
      // The community plugin passes GitHub's one-based page number.
      page: String(page),
    });

    if (branch) {
      query.set('branch', branch);
    }

    return this.get(
      `${this.repositoryPath(owner, repo)}/actions/runs?${query.toString()}`,
    );
  }

  async getWorkflow(
    options: Parameters<GithubActionsApi['getWorkflow']>[0],
  ): ReturnType<GithubActionsApi['getWorkflow']> {
    const { owner, repo, id } = options;
    return this.get(
      `${this.repositoryPath(owner, repo)}/actions/workflows/${id}`,
    );
  }

  async getWorkflowRun(
    options: Parameters<GithubActionsApi['getWorkflowRun']>[0],
  ): ReturnType<GithubActionsApi['getWorkflowRun']> {
    const { owner, repo, id } = options;
    return this.get(`${this.repositoryPath(owner, repo)}/actions/runs/${id}`);
  }

  async listJobsForWorkflowRun(
    options: Parameters<GithubActionsApi['listJobsForWorkflowRun']>[0],
  ): ReturnType<GithubActionsApi['listJobsForWorkflowRun']> {
    const { owner, repo, id, pageSize = 100, page = 1 } = options;
    const query = new URLSearchParams({
      per_page: String(pageSize),
      page: String(page),
    });

    return this.get(
      `${this.repositoryPath(
        owner,
        repo,
      )}/actions/runs/${id}/jobs?${query.toString()}`,
    );
  }

  async downloadJobLogsForWorkflowRun(
    options: Parameters<GithubActionsApi['downloadJobLogsForWorkflowRun']>[0],
  ): ReturnType<GithubActionsApi['downloadJobLogsForWorkflowRun']> {
    const { owner, repo, runId } = options;
    const response = await this.fetchApi.fetch(
      await this.proxyUrl(
        `${this.repositoryPath(owner, repo)}/actions/jobs/${runId}/logs`,
      ),
    );

    if (!response.ok) {
      throw new Error(`GitHub job-log request failed: ${response.status}`);
    }

    return response.text();
  }

  async listBranches(
    options: Parameters<GithubActionsApi['listBranches']>[0],
  ): ReturnType<GithubActionsApi['listBranches']> {
    const { owner, repo, page = 1 } = options;
    const query = new URLSearchParams({
      per_page: '100',
      page: String(page),
    });

    return this.get(
      `${this.repositoryPath(owner, repo)}/branches?${query.toString()}`,
    );
  }

  async getDefaultBranch(
    options: Parameters<GithubActionsApi['getDefaultBranch']>[0],
  ): ReturnType<GithubActionsApi['getDefaultBranch']> {
    const { owner, repo } = options;
    const repository = await this.get<{ default_branch: string }>(
      this.repositoryPath(owner, repo),
    );
    return repository.default_branch;
  }

  async reRunWorkflow(
    _options: Parameters<GithubActionsApi['reRunWorkflow']>[0],
  ): ReturnType<GithubActionsApi['reRunWorkflow']> {
    throw new Error(
      'Re-running workflows is disabled because Backstage uses a read-only GitHub token.',
    );
  }
}
